
import DOMPurify from 'dompurify';

/**
 * Validates and sanitizes CSS input with enhanced security measures
 * @param css Raw CSS string to validate
 * @returns Object containing sanitized CSS and validation status
 */
export const validateAndSanitizeCSS = (css: string): { css: string; isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  // Remove potentially dangerous patterns
  const sanitized = css
    // Block imports which could lead to external resource loading
    .replace(/@import\s+/gi, '/* @import blocked for security */')
    // Block all url() functions to prevent loading external resources
    .replace(/url\s*\([^)]*\)/gi, '/* url() blocked for security */')
    // Block expression() functions which could execute JavaScript
    .replace(/expression\s*\([^)]*\)/gi, '/* expression() blocked for security */')
    // Block all script injection attempts
    .replace(/<\/?script/gi, '/* script tag blocked for security */')
    // Block behaviors which could execute JavaScript
    .replace(/behavior\s*:/gi, '/* behavior blocked for security */')
    // Block -moz-binding which could load external resources
    .replace(/-moz-binding\s*:/gi, '/* -moz-binding blocked for security */');

  // Additional security check for potentially dangerous content
  if (/<[a-z/][\s\S]*>/i.test(css)) {
    errors.push('HTML tags are not allowed in CSS');
    return { css: '', isValid: false, errors };
  }

  // Use DOMPurify for additional sanitization
  const purified = DOMPurify.sanitize(sanitized, {
    ALLOWED_TAGS: [], // No HTML tags allowed
    ALLOWED_ATTR: [], // No attributes allowed
  });

  // Basic syntax validation
  try {
    const testElement = document.createElement('style');
    testElement.textContent = purified;
    document.head.appendChild(testElement);
    document.head.removeChild(testElement);
  } catch (error) {
    errors.push('Invalid CSS syntax');
    return { css: purified, isValid: false, errors };
  }

  return { css: purified, isValid: true, errors };
};
